/**
 * elementSlice.ts
 * 
 * Redux slice for managing elements data and state
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index';
import axios from 'axios';
import { Element } from '../../types/Element';

interface ElementState {
  elements: Element[];
  loading: boolean;
  error: string | null;
}

const initialState: ElementState = {
  elements: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchElements = createAsyncThunk('elements/fetchElements', async () => {
  const response = await axios.get<Element[]>('/api/elements');
  return response.data;
});

export const createElement = createAsyncThunk(
  'elements/createElement',
  async (element: Omit<Element, 'id'>) => {
    const response = await axios.post<Element>('/api/elements', element);
    return response.data;
  }
);

export const updateElement = createAsyncThunk(
  'elements/updateElement',
  async ({ id, element }: { id: string; element: Omit<Element, 'id'> }) => {
    const response = await axios.put<Element>(`/api/elements/${id}`, element);
    return response.data;
  }
);

export const deleteElement = createAsyncThunk(
  'elements/deleteElement',
  async (id: string) => {
    await axios.delete(`/api/elements/${id}`);
    return id;
  }
);

export const elementSlice = createSlice({
  name: 'elements',
  initialState,
  reducers: {
    clearElementError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch elements cases
      .addCase(fetchElements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchElements.fulfilled, (state, action: PayloadAction<Element[]>) => {
        state.loading = false;
        state.elements = action.payload;
      })
      .addCase(fetchElements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch elements';
      })
      
      // Create element cases
      .addCase(createElement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createElement.fulfilled, (state, action: PayloadAction<Element>) => {
        state.loading = false;
        state.elements.push(action.payload);
      })
      .addCase(createElement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create element';
      })
      
      // Update element cases
      .addCase(updateElement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateElement.fulfilled, (state, action: PayloadAction<Element>) => {
        state.loading = false;
        const index = state.elements.findIndex(
          (e) => e.atomicNumber === action.payload.atomicNumber
        );
        if (index !== -1) {
          state.elements[index] = action.payload;
        }
      })
      .addCase(updateElement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update element';
      })
      
      // Delete element cases
      .addCase(deleteElement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteElement.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.elements = state.elements.filter(
          (e) => e.atomicNumber.toString() !== action.payload
        );
      })
      .addCase(deleteElement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete element';
      });
  },
});

export const { clearElementError } = elementSlice.actions;

// Selectors
export const selectElements = (state: RootState) => state.elements.elements;
export const selectElementsLoading = (state: RootState) => state.elements.loading;
export const selectElementsError = (state: RootState) => state.elements.error;

export default elementSlice.reducer;
